/**
 * The fixed 10-color palette for Halfway (Rehab Tycoon).
 *
 * Art direction: 1960s UPA / Hanna-Barbera flat cartoon with the ATOMIC-AGE /
 * ATOMPUNK "World of Tomorrow" sensibility (canonical style block:
 * docs/DESIGN.md). Bold uniform charcoal outlines, flat fills, at most one
 * flat shadow tone. No gradients, no grime — satire lives in content.
 * Hexes locked by Russell's style block (2026-07-14). This palette is a HARD contract — every sprite, tint, and UI color
 * must resolve to one of these ten. Adding an eleventh color is a design
 * decision that goes through the RFC/exclusions ledger, not an ad-hoc literal.
 *
 * Each color is exposed twice:
 *   - `PALETTE_HEX`  — "#RRGGBB" strings, for CSS / HTML overlay.
 *   - `PALETTE`      — 0xRRGGBB numbers, for Phaser tints / fills.
 */

export const PALETTE_HEX = {
  cream: "#F4ECD8",
  paper: "#FBF6E9",
  teal: "#3AA7A0",
  mustard: "#E0A93B",
  coralRed: "#E2664E",
  green: "#6FA85B",
  gray: "#9AA0A6",
  charcoal: "#221F1A",
  skin: "#F0C39B",
  gold: "#F5C542",
} as const;

export type PaletteName = keyof typeof PALETTE_HEX;

/** Numeric 0xRRGGBB form, derived from PALETTE_HEX so the two never drift. */
export const PALETTE = Object.fromEntries(
  Object.entries(PALETTE_HEX).map(([name, hex]) => [
    name,
    Number.parseInt(hex.slice(1), 16),
  ]),
) as Record<PaletteName, number>;

/** The one flat shadow tone permitted by the art direction. */
export const SHADOW = PALETTE.charcoal;

/** Canonical outline color + weight — applied uniformly across all art. */
export const OUTLINE = {
  color: PALETTE.charcoal,
  hex: PALETTE_HEX.charcoal,
} as const;
